/* =============================================================================
 * a11y.js — Accessibility behaviour layer for the Blockshop theme (Townhouse)
 *
 * Loaded (deferred) AFTER theme.min.js from layout/theme.liquid. It leaves the
 * minified theme bundle untouched and adds only the behaviour the audit found
 * missing for WCAG 2.2 AA:
 *   1. Keyboard (Enter/Space) activation for role="button" controls that are
 *      not native buttons and were previously click-only.
 *   2. Dialog semantics for the off-canvas drawer, generic modal, search modal
 *      and newsletter popup: focus move-in, focus trap, Escape-to-close, and
 *      focus return to the opener.
 *   3. aria-expanded syncing on the drawer triggers, aria-hidden on closed
 *      drawers, and restoring focusability to inputs the theme excludes.
 *   4. Polite/assertive live regions announcing cart, search and feedback-bar
 *      updates that were previously silent.
 *   6. A visible, keyboard-operable pause/play control for the auto-rotating
 *      slideshow, plus pause-on-hover/focus and auto-pause under reduced-motion.
 *   7. Working prev/next controls for overflowing Owl carousels, whose only
 *      pointer affordance was dragging (2.5.7, and 2.5.1 at Level A).
 *   8. Making cart quantity fields safe to tab through: the theme re-renders the
 *      cart on every blur of those fields, which turned into a keyboard trap the
 *      moment the fields became focusable.
 *
 * Round 2 (2026-07-30) addresses audit findings F1, F2, F3, F6, F8, F13 and F14.
 * See docs/accessibility-audit-2026-07.md for the evidence behind each.
 *
 * Written in defensive vanilla JS. It feature-detects everything and never
 * throws if an element/library is absent. jQuery (used by the theme) is used
 * only where it is the only reliable way to reach the Owl carousel instance.
 * ============================================================================= */
(function () {
  'use strict';

  if (typeof document === 'undefined' || !document.addEventListener) return;

  var FOCUSABLE = [
    'a[href]', 'area[href]', 'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])', 'select:not([disabled])',
    'textarea:not([disabled])', 'summary', 'iframe', 'audio[controls]',
    'video[controls]', 'model-viewer', '[tabindex]:not([tabindex="-1"])',
    '[role="button"]:not([disabled])'
  ].join(',');

  var reduceMotion = window.matchMedia
    ? window.matchMedia('(prefers-reduced-motion: reduce)')
    : { matches: false };

  // Defined up here (not next to the slideshow code) because this script is
  // deferred: ready() runs setup synchronously the moment it is called, before
  // later `var` assignments execute. Declaring these below would leave them
  // undefined at init and render the literal text "undefined" in the button.
  var ICON_PLAY = '<svg viewBox="0 0 16 16" aria-hidden="true" focusable="false"><path d="M3 2l11 6L3 14z"/></svg>';
  var ICON_PAUSE = '<svg viewBox="0 0 16 16" aria-hidden="true" focusable="false"><path d="M3 2h4v12H3zM9 2h4v12H9z"/></svg>';
  var ICON_PREV = '<svg viewBox="0 0 16 16" aria-hidden="true" focusable="false"><path d="M10.5 1.5L4 8l6.5 6.5" fill="none" stroke="currentColor" stroke-width="2"/></svg>';
  var ICON_NEXT = '<svg viewBox="0 0 16 16" aria-hidden="true" focusable="false"><path d="M5.5 1.5L12 8l-6.5 6.5" fill="none" stroke="currentColor" stroke-width="2"/></svg>';

  function ready(fn) {
    if (document.readyState !== 'loading') { fn(); }
    else { document.addEventListener('DOMContentLoaded', fn); }
  }

  function isVisible(el) {
    if (!el) return false;
    if (!(el.offsetWidth || el.offsetHeight || el.getClientRects().length)) return false;
    var cs = window.getComputedStyle(el);
    return cs.visibility !== 'hidden' && cs.display !== 'none';
  }

  // Stricter check for "is this dialog actually open?". Several dialog roots are
  // only opacity:0 / pointer-events:none while closed (and, before the async
  // stylesheet loads, are not yet display:none), so geometry alone would report
  // them visible on page load and wrongly move focus to the bottom of the page.
  function isDialogOpen(el) {
    if (!isVisible(el)) return false;
    var cs = window.getComputedStyle(el);
    if (parseFloat(cs.opacity) === 0) return false;
    if (cs.pointerEvents === 'none') return false;
    return true;
  }

  function focusables(container) {
    if (!container) return [];
    return Array.prototype.filter.call(
      container.querySelectorAll(FOCUSABLE),
      function (el) {
        return isVisible(el) &&
          el.getAttribute('tabindex') !== '-1' &&
          !(el.closest && el.closest('[aria-hidden="true"]'));
      }
    );
  }

  function focusFirst(container) {
    var f = focusables(container);
    if (f.length) { f[0].focus(); return true; }
    if (container && container.getAttribute) {
      if (!container.hasAttribute('tabindex')) container.setAttribute('tabindex', '-1');
      container.focus();
      return true;
    }
    return false;
  }

  // Server-rendered form errors surfaced on reload. Shopify customer forms POST and
  // reload, so a validation error sits in the initial HTML and nothing mutates — the
  // live-region and feedback-bar observers only fire on mutation, so neither catches
  // it. Move focus to the invalid field (which carries aria-invalid + aria-describedby)
  // so the screen reader announces the error and the user lands ready to correct it.
  // This is post-reload focus management, not AJAX or intercepting the submit.
  function focusServerFormError() {
    var err = document.getElementById('footer-newsletter-error');
    if (!err) return;
    var text = (err.textContent || '').replace(/\s+/g, ' ').trim();
    var field = document.getElementById('footer-email');
    window.setTimeout(function () {
      // Primary, reliable path: write the error into the assertive live region. After
      // the reload the message is already in the DOM, so this fresh change is what
      // VoiceOver actually speaks — a programmatic focus alone can be missed amid the
      // page-load announcement.
      if (text) announce(text, true);
      // Then move focus so the user lands ready to correct it.
      if (field && isVisible(field)) field.focus();
      else if (isVisible(err)) {
        if (!err.hasAttribute('tabindex')) err.setAttribute('tabindex', '-1');
        err.focus();
      }
    }, 300);
  }

  // Off-screen sr-only containers (data-item="hidden-text", positioned at left:-10000px) can
  // still hold keyboard-focusable links — e.g. the reCAPTCHA disclaimer's Google Privacy/Terms
  // links — so Tab lands on something invisible. Take them out of the tab order; they stay in
  // the accessibility tree, so the disclaimer is still read.
  function deactivateOffscreenLinks() {
    var links = document.querySelectorAll('[data-item="hidden-text"] a[href]');
    Array.prototype.forEach.call(links, function (a) {
      a.setAttribute('tabindex', '-1');
    });
  }

  ready(function () {
    try { deactivateOffscreenLinks(); } catch (e) {}
    try { trackLastInteractive(); } catch (e) {}
    try { setupKeyboardActivation(); } catch (e) {}
    try { setupLiveRegions(); } catch (e) {}
    try { setupOffCanvas(); } catch (e) {}
    try { setupModalDialogs(); } catch (e) {}
    try { setupSlideshow(); } catch (e) {}
    try { setupCarouselControls(); } catch (e) {}
    try { setupCartQuantityFields(); } catch (e) {}
    try { focusServerFormError(); } catch (e) {}
  });

  /* -------------------------------------------------------------------------
   * 8. Cart quantity fields — make them safe to tab through.
   *
   *    assets/theme.js (FrameworkCart.inputBoxListener) binds focusout on
   *    .cart--quantity--input and UNCONDITIONALLY fires updateQuantity(), which
   *    calls updateAllHtml() and replaces the whole cart markup. That was harmless
   *    while the field carried tabindex="-1" and could never be focused. Once it
   *    became reachable (F8 / 2.1.1), every tab-through blurred the field, re-rendered
   *    the cart, and destroyed focus — so you could not get past the first quantity
   *    field at all. Making the field keyboard-accessible created a keyboard trap.
   *
   *    Fixed at the point of submission rather than by interfering with the event:
   *    the theme's own focusout handler is unbound and re-bound behind a guard that
   *    calls it only when the value actually changed. Merely passing through a field
   *    never mutates the cart. When the value HAS changed the theme's original
   *    handler runs untouched, so the update behaves exactly as it always did, and
   *    focus is recovered afterwards because the re-render drops it to <body>.
   *
   *    Wrapping rather than stopping the event matters: focusout is a shared, bubbling
   *    event, and suppressing it wholesale would silently affect anything else that
   *    listens for it.
   * ---------------------------------------------------------------------- */
  var QTY_SEL = '.cart--quantity--input';

  function isQtyField(el) {
    return el && el.matches && el.matches(QTY_SEL);
  }

  // Numeric-keypad digits plus keys the theme's keydown filter rejects outright.
  var QTY_ALLOWED_KEYS = [46 /* Delete */, 36 /* Home */, 35 /* End */, 38 /* Up */, 40 /* Down */];

  function isAllowedQtyKey(e) {
    var k = e.which || e.keyCode;
    return (k >= 96 && k <= 105) || QTY_ALLOWED_KEYS.indexOf(k) !== -1;
  }

  // Wrap the theme's handlers on one field. Returns true if it was (or already is)
  // guarded, false if jQuery's handler store was unreachable.
  function guardQuantityField(el) {
    var $ = window.jQuery;
    if (!$ || typeof $._data !== 'function') return false;
    if (el.__a11yQtyGuarded) return true;

    var events = $._data(el, 'events');
    if (!events) return true; // nothing bound yet; the observer will retry

    var originalFocusout = (events.focusout || []).map(function (h) { return h.handler; });
    var originalKeydown = (events.keydown || []).map(function (h) { return h.handler; });
    if (!originalFocusout.length && !originalKeydown.length) return true;

    el.__a11yQtyGuarded = true;

    if (originalFocusout.length) {
      $(el).off('focusout');
      $(el).on('focusout', function (e) {
        var baseline = el.__a11yBaseline != null ? el.__a11yBaseline : el.defaultValue;
        if (String(el.value).trim() === String(baseline).trim()) return; // unchanged — do not submit

        el.__a11yBaseline = el.value;
        for (var i = 0; i < originalFocusout.length; i++) originalFocusout[i].call(el, e);

        var item = el.closest('.cart--item');
        var line = item && item.getAttribute('data-line-num');
        if (line) recoverCartFocus(line);
      });
    }

    // The theme's keydown filter allows only 0-9 (48-57), Backspace, Left, Right and
    // Tab, which also rejects the numeric keypad (96-105), Delete, Home, End and
    // Up/Down — a numpad user cannot type a quantity at all. Skip the original for
    // those keys so the browser's default behaviour stands.
    if (originalKeydown.length) {
      $(el).off('keydown');
      $(el).on('keydown', function (e) {
        if (isAllowedQtyKey(e)) return;
        for (var i = 0; i < originalKeydown.length; i++) originalKeydown[i].call(el, e);
      });
    }

    return true;
  }

  function guardAllQuantityFields() {
    var ok = true;
    Array.prototype.forEach.call(document.querySelectorAll(QTY_SEL), function (el) {
      if (!guardQuantityField(el)) ok = false;
    });
    return ok;
  }

  function setupCartQuantityFields() {
    // Record the value the user started with, per field.
    document.addEventListener('focusin', function (e) {
      if (isQtyField(e.target)) e.target.__a11yBaseline = e.target.value;
    }, true);

    var wrapped = guardAllQuantityFields();

    // The cart re-renders on every update, so fields are replaced and lose their
    // wrapper. Re-apply whenever cart markup changes.
    Array.prototype.forEach.call(document.querySelectorAll('.cart--root'), function (root) {
      new MutationObserver(debounce(guardAllQuantityFields, 80))
        .observe(root, { childList: true, subtree: true });
    });
    // The theme binds its handlers when each Cart instance initialises, which may be
    // after this runs; retry briefly so we wrap rather than miss them.
    var tries = 0;
    var retry = window.setInterval(function () {
      tries += 1;
      guardAllQuantityFields();
      if (tries > 10) window.clearInterval(retry);
    }, 200);

    if (!wrapped) fallbackQuantityGuard();
  }

  // Only used when jQuery's handler store is unreachable (e.g. a future jQuery that
  // drops _data). Less surgical — it suppresses the event rather than the submit —
  // so it is deliberately the fallback, not the primary.
  function fallbackQuantityGuard() {
    document.addEventListener('focusout', function (e) {
      var el = e.target;
      if (!isQtyField(el) || el.__a11yQtyGuarded) return;
      var baseline = el.__a11yBaseline != null ? el.__a11yBaseline : el.defaultValue;
      if (String(el.value).trim() === String(baseline).trim()) {
        e.stopImmediatePropagation();
      }
    }, true);
    document.addEventListener('keydown', function (e) {
      if (!isQtyField(e.target) || e.target.__a11yQtyGuarded) return;
      if (isAllowedQtyKey(e)) e.stopImmediatePropagation();
    }, true);
  }

  // After a legitimate quantity change the theme replaces the cart markup, so
  // whatever had focus no longer exists and focus falls to <body>. Put it back on
  // the same line's quantity field. Only acts if focus really was lost, so it never
  // fights a focus move the user made deliberately.
  function recoverCartFocus(line) {
    var tries = 0;
    var timer = window.setInterval(function () {
      tries += 1;
      if (tries > 15) { window.clearInterval(timer); return; }
      if (document.activeElement && document.activeElement !== document.body) {
        window.clearInterval(timer);
        return;
      }
      var candidates = document.querySelectorAll(
        '.cart--item[data-line-num="' + line + '"] .cart--quantity--input'
      );
      for (var i = 0; i < candidates.length; i++) {
        if (isVisible(candidates[i])) {
          candidates[i].focus();
          window.clearInterval(timer);
          return;
        }
      }
    }, 120);
  }

  /* -------------------------------------------------------------------------
   * 1. Keyboard activation for div/anchor "buttons" that are click-only.
   *    These selectors were verified to have NO existing Enter/Space handling
   *    in the theme bundle, so there is no risk of double-activation.
   * ---------------------------------------------------------------------- */
  function setupKeyboardActivation() {
    var SEL = '.off-canvas--open, .modal--close, .modal--prev, .modal--next';
    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Enter' && e.key !== ' ' && e.key !== 'Spacebar') return;
      var target = e.target;
      if (!target || !target.closest) return;
      var el = target.closest(SEL);
      if (!el) return;
      // Never hijack keys while typing in a field.
      if (/^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName)) return;
      e.preventDefault(); // stop Space from scrolling
      el.click();
    });
  }

  /* -------------------------------------------------------------------------
   * 4. Live regions.
   * ---------------------------------------------------------------------- */
  var politeRegion, assertiveRegion;

  function makeRegion(assertive) {
    var r = document.createElement('div');
    r.className = 'a11y-visually-hidden';
    r.setAttribute('aria-live', assertive ? 'assertive' : 'polite');
    r.setAttribute('aria-atomic', 'true');
    r.setAttribute('role', assertive ? 'alert' : 'status');
    document.body.appendChild(r);
    return r;
  }

  function announce(msg, assertive) {
    var r = assertive ? assertiveRegion : politeRegion;
    if (!r || !msg) return;
    // Clear then set on next frame so repeated identical messages re-announce.
    r.textContent = '';
    window.requestAnimationFrame(function () { r.textContent = msg; });
  }

  function debounce(fn, wait) {
    var t;
    return function () {
      var ctx = this, args = arguments;
      clearTimeout(t);
      t = setTimeout(function () { fn.apply(ctx, args); }, wait);
    };
  }

  function setupLiveRegions() {
    if (!document.body) return;
    politeRegion = makeRegion(false);
    assertiveRegion = makeRegion(true);

    // Cart item-count changes.
    var counters = document.querySelectorAll('.cart--external--total-items');
    Array.prototype.forEach.call(counters, function (node) {
      var last = (node.textContent || '').trim();
      var obs = new MutationObserver(debounce(function () {
        var now = (node.textContent || '').trim();
        if (now === last) return;
        last = now;
        var n = parseInt(now, 10);
        if (isNaN(n)) return;
        announce('Cart updated, ' + n + (n === 1 ? ' item' : ' items'));
      }, 250));
      obs.observe(node, { childList: true, characterData: true, subtree: true });
    });

    // Feedback bar (newsletter/contact success + generic feedback).
    var feedback = document.querySelector('[data-js-class="FrameworkFeedbackBar"], .feedback-bar, .feedback-bar--root');
    if (feedback) {
      var fobs = new MutationObserver(debounce(function () {
        var open = feedback.getAttribute('data-open');
        if (open === 'true' || feedback.classList.contains('open') || isVisible(feedback)) {
          var text = (feedback.textContent || '').replace(/\s+/g, ' ').trim();
          if (text) announce(text, true);
        }
      }, 150));
      fobs.observe(feedback, { attributes: true, attributeFilter: ['data-open', 'class', 'style'] });
    }

    // Search results (modal / ajax) — announce the count as results populate.
    var searchResults = document.querySelector('.search--results');
    if (searchResults) {
      var sobs = new MutationObserver(debounce(function () {
        var items = searchResults.querySelectorAll('.search--product, .search--article');
        var summary = document.querySelector('.search--results-summary');
        if (summary && (summary.textContent || '').trim()) {
          announce((summary.textContent || '').replace(/\s+/g, ' ').trim());
        } else {
          announce(items.length + (items.length === 1 ? ' result' : ' results'));
        }
      }, 400));
      sobs.observe(searchResults, { childList: true, subtree: true });
    }
  }

  /* -------------------------------------------------------------------------
   * 3. Off-canvas drawer: aria-expanded sync, Escape-to-close, focus move-in
   *    and focus-return. The theme's own focus-trigger sentinels keep focus
   *    trapped, so we do NOT add a second Tab trap here.
   * ---------------------------------------------------------------------- */
  function setupOffCanvas() {
    var viewport = document.querySelector('.off-canvas--viewport');
    if (!viewport) return;
    var closeBtn = viewport.querySelector('.off-canvas--close');
    var lastOpener = null;
    var wasOpen = false;

    var PANEL_IDS = ['off-canvas-left', 'off-canvas-right'];

    // Openers are matched by what they control rather than by class. The header
    // cart trigger is an <a> (not .off-canvas--open) and the hamburger is a div,
    // so aria-controls is the only thing they reliably share.
    function openersFor(panelId) {
      return Array.prototype.slice.call(
        document.querySelectorAll('[aria-controls="' + panelId + '"]')
      );
    }

    // F13: aria-expanded was previously set from `lastOpener`, which was only
    // populated by a click listener. Verified live: the theme handles Enter on the
    // openers itself and stops propagation, so no click event is ever dispatched on
    // keyboard activation and lastOpener stayed null — the attribute never updated.
    // Deriving the open panel from the state attribute instead works for pointer and
    // keyboard alike, and needs no knowledge of how activation happened.
    function syncExpanded(openPanelId) {
      PANEL_IDS.forEach(function (id) {
        openersFor(id).forEach(function (o) {
          if (o.hasAttribute('aria-expanded')) {
            o.setAttribute('aria-expanded', id === openPanelId ? 'true' : 'false');
          }
        });
      });
    }

    function currentState() {
      var s = viewport.getAttribute('data-off-canvas--state');
      return s && s !== 'closed' ? s : '';
    }

    function openPanelId() {
      return /right/i.test(currentState()) ? 'off-canvas-right' : 'off-canvas-left';
    }

    function openPanel() {
      return document.getElementById(openPanelId());
    }

    // F6: a closed drawer stays in the DOM as display:block/visibility:visible,
    // merely translated off-screen. Its <h1 class="cart--title">Your Cart</h1> was
    // therefore exposed on every page of the site, giving every template two h1s —
    // and on /search, where the template emits none of its own, it was the ONLY h1.
    // Hiding closed panels from the accessibility tree fixes the heading, the links
    // and the landmarks in one go. The theme already manages tabindex separately.
    function setPanelHidden(panel, hidden) {
      if (!panel) return;
      // Never hide a subtree that contains focus — that strands the focus ring in an
      // aria-hidden region, which is worse than the problem being fixed.
      if (hidden && panel.contains(document.activeElement)) return;
      if (hidden) panel.setAttribute('aria-hidden', 'true');
      else panel.removeAttribute('aria-hidden');
    }

    function applyHiddenState() {
      var openId = currentState() ? openPanelId() : null;
      PANEL_IDS.forEach(function (id) {
        setPanelHidden(document.getElementById(id), id !== openId);
      });
    }

    // F8: assets/theme.js restores focusability on drawer open with
    // `.find('[tabindex="-1"]:not(input)')` — inputs are deliberately excluded, so the
    // cart quantity field stays unreachable even when the drawer is open. Undo that
    // exclusion for the panel that just opened.
    function restoreInputTabindex(panel) {
      if (!panel) return;
      Array.prototype.forEach.call(
        panel.querySelectorAll('input[tabindex="-1"], textarea[tabindex="-1"], select[tabindex="-1"]'),
        function (el) {
          if (el.type === 'hidden') return;
          el.removeAttribute('tabindex');
        }
      );
    }

    function onOpen() {
      var panel = openPanel();
      syncExpanded(openPanelId());
      applyHiddenState();
      window.setTimeout(function () {
        restoreInputTabindex(panel);
        var active = document.activeElement;
        if (panel && (!active || !panel.contains(active))) {
          if (closeBtn && isVisible(closeBtn)) closeBtn.focus();
          else focusFirst(panel);
        }
      }, 60);
      // Second pass: the theme's own tabindex restore runs from its open handler and
      // the ordering relative to this observer is not guaranteed. Re-applying costs
      // nothing and stops the theme's `:not(input)` exclusion winning the race.
      window.setTimeout(function () { restoreInputTabindex(panel); }, 350);
    }

    function onClose() {
      syncExpanded(null);
      if (lastOpener && typeof lastOpener.focus === 'function' && isVisible(lastOpener)) {
        lastOpener.focus();
      }
      lastOpener = null;
      // Re-hide after focus has left the panel, or setPanelHidden will decline.
      window.setTimeout(applyHiddenState, 80);
    }

    // Track the opener for focus-return. Pointer and keyboard are both covered:
    // keydown is needed because the theme suppresses the click on Enter.
    function noteOpener(e) {
      var t = e.target;
      if (!t || !t.closest) return;
      var o = t.closest('[aria-controls="off-canvas-left"], [aria-controls="off-canvas-right"], .off-canvas--open');
      if (o) lastOpener = o;
    }
    document.addEventListener('click', noteOpener, true);
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') noteOpener(e);
    }, true);

    var obs = new MutationObserver(function () {
      var open = !!currentState();
      if (open && !wasOpen) { wasOpen = true; onOpen(); }
      else if (!open && wasOpen) { wasOpen = false; onClose(); }
    });
    obs.observe(viewport, { attributes: true, attributeFilter: ['data-off-canvas--state'] });

    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape' && e.key !== 'Esc') return;
      if (!currentState()) return;
      if (closeBtn) closeBtn.click();
    });

    // Establish the correct initial state (both panels closed on load).
    applyHiddenState();
  }

  /* -------------------------------------------------------------------------
   * 2. Modal / search / popup dialogs: full focus trap + Escape + return.
   *    These had NO focus management in the theme. Visibility is detected by
   *    observing body/root class & style changes and re-checking isVisible().
   * ---------------------------------------------------------------------- */
  // Last element the user actually activated, used for dialog focus-return (F1).
  // Tracked on both pointer and keyboard because the theme suppresses the click
  // event when a control is activated with Enter.
  var lastInteractive = null;

  function trackLastInteractive() {
    function note(e) {
      var t = e.target;
      if (!t || !t.closest) return;
      var el = t.closest(FOCUSABLE);
      if (el && isVisible(el)) lastInteractive = el;
    }
    document.addEventListener('pointerdown', note, true);
    document.addEventListener('mousedown', note, true);
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') note(e);
    }, true);
  }

  function setupModalDialogs() {
    // Only manage dialogs whose CLOSED state is display:none, so they can never
    // be mistaken for "open" on load. (.modal--root / .modal.video toggle via
    // opacity and are handled by the theme; we skip them to avoid a false open.)
    var ROOTS = '.modal--window, .popup--root';
    var roots = Array.prototype.slice.call(document.querySelectorAll(ROOTS));
    if (!roots.length) return;

    var openState = new WeakMap ? new WeakMap() : null;
    var stateFor = {};
    var idSeq = 0;

    function getState(root) {
      if (openState) return openState.get(root);
      return stateFor[root.__a11yId];
    }
    function setState(root, val) {
      if (openState) { openState.set(root, val); return; }
      if (!root.__a11yId) root.__a11yId = 'd' + (++idSeq);
      stateFor[root.__a11yId] = val;
    }

    function closeControlFor(root) {
      return root.querySelector('.modal--close, .close, [data-modal-custom-close]') ||
        document.querySelector('.popup-modal-close');
    }

    // F2: .modal--window is a single shared shell. The theme moves each dialog's
    // content into it on open, so its hardcoded aria-label ("Product media") named
    // the search dialog too. The correct name is published by the source wrapper as
    // data-modal-label; fall back to the shell's own original label, then a heading,
    // then a generic. Recomputed on every open so it can never go stale.
    function contextualLabelFor(root) {
      var source = null;
      var candidates = document.querySelectorAll('[data-modal-label]');
      Array.prototype.forEach.call(candidates, function (c) {
        if (!source && isDialogOpen(c)) source = c;
      });
      if (source) {
        var l = (source.getAttribute('data-modal-label') || '').trim();
        if (l) return l;
      }
      if (root.__a11yDefaultLabel) return root.__a11yDefaultLabel;
      var h = root.querySelector('h1, h2, [data-item="section-heading"]');
      if (h && (h.textContent || '').trim()) {
        return (h.textContent || '').replace(/\s+/g, ' ').trim();
      }
      return 'Dialog';
    }

    function ensureDialogSemantics(root) {
      if (!root.getAttribute('role')) root.setAttribute('role', 'dialog');
      if (!root.getAttribute('aria-modal')) root.setAttribute('aria-modal', 'true');
      // Remember the label authored in Liquid before overwriting it.
      if (root.__a11yDefaultLabel === undefined) {
        root.__a11yDefaultLabel = (root.getAttribute('aria-label') || '').trim() || null;
      }
      if (!root.getAttribute('aria-labelledby')) {
        root.setAttribute('aria-label', contextualLabelFor(root));
      }
    }

    function open(root) {
      if (getState(root)) return;
      // F1: activeElement is unreliable here — by the time a dialog is detected as
      // open the theme may already have moved focus inside it (the search modal
      // focuses its textbox), so the "opener" recorded was the textbox or <body> and
      // focus-return on Escape sent focus to nowhere. Prefer the last element the
      // user actually activated.
      var opener = document.activeElement;
      if (!opener || opener === document.body || root.contains(opener)) {
        opener = lastInteractive || opener;
      }
      var trap = function (e) {
        if (e.key === 'Escape' || e.key === 'Esc') {
          var c = closeControlFor(root);
          if (c) { e.preventDefault(); c.click(); }
          return;
        }
        if (e.key !== 'Tab') return;
        // A Shopify cookie-consent banner (#shopify-pc__banner) sits OUTSIDE the dialog
        // but must stay keyboard-reachable — otherwise the newsletter popup's trap locks
        // users out of the consent controls they have to act on first. When a visible
        // banner is present, fold its controls into the trap so Tab cycles through the
        // dialog AND the banner (in DOM order), but never the masked background behind.
        var banner = document.querySelector('#shopify-pc__banner');
        var extra = (banner && banner !== root && !root.contains(banner) && isDialogOpen(banner))
          ? focusables(banner) : [];
        var f = focusables(root).concat(extra);
        if (extra.length) {
          f.sort(function (a, b) {
            return (a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING) ? -1 : 1;
          });
        }
        if (!f.length) { e.preventDefault(); return; }
        var first = f[0], last = f[f.length - 1];
        var active = document.activeElement;
        var inScope = root.contains(active) || (extra.length && banner.contains(active));
        if (e.shiftKey && active === first) {
          e.preventDefault(); last.focus();
        } else if (!e.shiftKey && active === last) {
          e.preventDefault(); first.focus();
        } else if (!inScope) {
          e.preventDefault(); first.focus();
        }
      };
      setState(root, { opener: opener, trap: trap });
      ensureDialogSemantics(root);
      document.addEventListener('keydown', trap, true);
      window.setTimeout(function () {
        if (!root.contains(document.activeElement)) focusFirst(root);
      }, 60);
    }

    function close(root) {
      var st = getState(root);
      if (!st) return;
      document.removeEventListener('keydown', st.trap, true);
      setState(root, null);
      if (st.opener && typeof st.opener.focus === 'function' && isVisible(st.opener)) {
        st.opener.focus();
      }
    }

    var rescan = debounce(function () {
      roots.forEach(function (root) {
        var vis = isDialogOpen(root);
        if (vis && !getState(root)) open(root);
        else if (!vis && getState(root)) close(root);
      });
    }, 50);

    // Observe the roots themselves and body (theme toggles body classes like
    // `modal-open` / `popup-modal`) for anything that could change visibility.
    roots.forEach(function (root) {
      new MutationObserver(rescan).observe(root, {
        attributes: true, attributeFilter: ['class', 'style', 'data-view', 'data-open']
      });
    });
    if (document.body) {
      new MutationObserver(rescan).observe(document.body, {
        attributes: true, attributeFilter: ['class', 'style']
      });
    }
    rescan();
  }

  /* -------------------------------------------------------------------------
   * 6. Slideshow pause/play (WCAG 2.2.2). Owl Carousel 1.x auto-rotation.
   *    (ICON_PLAY / ICON_PAUSE are declared near the top of this file.)
   * ---------------------------------------------------------------------- */

  function owlInstance(root) {
    if (!window.jQuery) return null;
    var $ = window.jQuery;
    var candidates = [root.querySelector('.slides'), root.querySelector('.owl-carousel'), root];
    for (var i = 0; i < candidates.length; i++) {
      var c = candidates[i];
      if (!c) continue;
      var inst = $(c).data('owlCarousel') || $(c).data('owl.carousel') || $(c).data('owlcarousel');
      if (inst) return inst;
    }
    return null;
  }

  function owlStop(root) {
    var inst = owlInstance(root);
    try {
      if (inst && typeof inst.stop === 'function') { inst.stop(); return; }
      if (window.jQuery) window.jQuery(root.querySelector('.slides') || root).trigger('stop.owl.autoplay');
    } catch (e) {}
  }

  function owlPlay(root, freq) {
    var inst = owlInstance(root);
    try {
      if (inst && typeof inst.play === 'function') { inst.play(freq); return; }
      if (window.jQuery) window.jQuery(root.querySelector('.slides') || root).trigger('play.owl.autoplay', [freq]);
    } catch (e) {}
  }

  function setupSlideshow() {
    var sliders = document.querySelectorAll('section.slider[data-autoplay="true"]');
    Array.prototype.forEach.call(sliders, function (root) {
      if (root.querySelector('.a11y-slideshow-toggle')) return;
      var freq = parseInt(root.getAttribute('data-rotate-frequency'), 10) || 7000;

      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'a11y-slideshow-toggle';
      var userPaused = reduceMotion.matches; // start paused if reduced-motion

      function render() {
        var paused = userPaused;
        btn.innerHTML = paused ? ICON_PLAY : ICON_PAUSE;
        btn.setAttribute('aria-pressed', paused ? 'true' : 'false');
        btn.setAttribute('aria-label', paused ? 'Play slideshow' : 'Pause slideshow');
      }

      function applyPause() { owlStop(root); }
      function applyPlay() { if (!userPaused) owlPlay(root, freq); }

      btn.addEventListener('click', function () {
        userPaused = !userPaused;
        render();
        if (userPaused) applyPause(); else owlPlay(root, freq);
      });

      // Pause on hover/focus, resume only if the user hasn't explicitly paused.
      root.addEventListener('mouseenter', applyPause);
      root.addEventListener('mouseleave', applyPlay);
      root.addEventListener('focusin', applyPause);
      root.addEventListener('focusout', function (e) {
        if (!root.contains(e.relatedTarget)) applyPlay();
      });

      // React to a live change of the reduced-motion preference.
      var onRM = function () { if (reduceMotion.matches && !userPaused) { userPaused = true; render(); applyPause(); } };
      if (reduceMotion.addEventListener) reduceMotion.addEventListener('change', onRM);
      else if (reduceMotion.addListener) reduceMotion.addListener(onRM);

      root.style.position = root.style.position || 'relative';
      root.appendChild(btn);
      render();

      // Owl initialises asynchronously; enforce the initial paused state shortly after.
      if (userPaused) window.setTimeout(applyPause, 800);
    });
  }

  /* -------------------------------------------------------------------------
   * 7. Carousel prev/next controls (WCAG 2.5.7 Dragging Movements, and 2.5.1
   *    Pointer Gestures at Level A).
   *
   *    The featured-collection carousel holds 20 products in an 11800px wrapper
   *    inside a 1180px container — 4 visible at a time. Owl's own .owl-prev /
   *    .owl-next are rendered 0x0 with tabindex="-1" and no accessible name, and a
   *    programmatic click on them does nothing. There are no pagination dots and no
   *    native scrollbar, so dragging was the only pointer route to items 5-20, and
   *    keyboard users could only tab through items one at a time.
   *
   *    This injects real buttons wired to the Owl instance. Applied to any Owl
   *    carousel whose content overflows its container, so product sliders elsewhere
   *    get the same treatment.
   * ---------------------------------------------------------------------- */
  /* ICON_PREV / ICON_NEXT are declared at the top of this file, with ICON_PLAY and
     ICON_PAUSE — see the note there. ready() runs synchronously on a deferred script,
     so any var assigned below this point is still undefined at init. */

  function owlMove(carousel, dir) {
    var inst = owlInstance(carousel);
    try {
      if (inst) {
        // Verified live on Owl 1.x: next()/prev() move the wrapper and update
        // currentItem (0 -> 4 -> 0 on the 20-item, 4-visible product carousel).
        if (dir > 0 && typeof inst.next === 'function') { inst.next(); return true; }
        if (dir < 0 && typeof inst.prev === 'function') { inst.prev(); return true; }
      }
      if (window.jQuery) {
        window.jQuery(carousel).trigger(dir > 0 ? 'owl.next' : 'owl.prev');
        return true;
      }
    } catch (e) {}
    return false;
  }

  // Whether a carousel has more items than it shows, and therefore needs controls.
  //
  // Do NOT infer this from wrapper vs container width: Owl 1.x sizes the wrapper at
  // roughly double the container even for a single slide, so the single-slide
  // slideshow measures 2560px inside 1280px and would wrongly get nav buttons.
  // The instance's own counts are exact — itemsAmount vs options.items (this build
  // exposes itemsAmount and $owlItems, not owlItems).
  function needsCarouselNav(carousel) {
    var inst = owlInstance(carousel);
    if (inst && inst.options && typeof inst.options.items === 'number') {
      var total = typeof inst.itemsAmount === 'number'
        ? inst.itemsAmount
        : carousel.querySelectorAll('.owl-item').length;
      return total > inst.options.items;
    }
    // No instance reachable — fall back to counting DOM items against a rough
    // estimate of how many fit, rather than trusting wrapper width.
    var items = carousel.querySelectorAll('.owl-item');
    if (items.length < 2) return false;
    var itemW = items[0].getBoundingClientRect().width || 1;
    var fits = Math.round(carousel.getBoundingClientRect().width / itemW);
    return items.length > Math.max(1, fits);
  }

  function injectCarouselNav(carousel) {
    if (carousel.parentElement.querySelector('.a11y-carousel-nav')) return true;
    if (!needsCarouselNav(carousel)) return false;

    var nav = document.createElement('div');
    nav.className = 'a11y-carousel-nav';

    function makeBtn(dir, label, icon) {
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'a11y-carousel-btn a11y-carousel-btn--' + (dir > 0 ? 'next' : 'prev');
      b.setAttribute('aria-label', label);
      b.innerHTML = icon;
      b.addEventListener('click', function () { owlMove(carousel, dir); });
      return b;
    }

    nav.appendChild(makeBtn(-1, 'Previous products', ICON_PREV));
    nav.appendChild(makeBtn(1, 'Next products', ICON_NEXT));

    var host = carousel.parentElement;
    if (window.getComputedStyle(host).position === 'static') host.style.position = 'relative';
    host.appendChild(nav);
    return true;
  }

  function setupCarouselControls() {
    // Two separate timing traps here, both of which bit in production:
    //
    //  1. `.owl-carousel` is NOT in the server-rendered HTML — Owl adds the class when it
    //     initialises. Querying for it once at ready() therefore matches nothing, so the
    //     element list must be re-queried on every tick rather than captured up front.
    //  2. owl.carousel.min.js is injected dynamically, so the Owl instance may not exist
    //     for well over a second. needsCarouselNav() is only trustworthy once it does;
    //     asked earlier it falls through to its DOM fallback and answers "no".
    //
    // Hence: poll, re-query each time, and only decide a carousel once its instance is
    // reachable. Each carousel is marked so it is judged exactly once.
    var tries = 0;
    var timer = window.setInterval(function () {
      tries += 1;
      var carousels = document.querySelectorAll('.owl-carousel');
      var allResolved = carousels.length > 0;

      Array.prototype.forEach.call(carousels, function (c) {
        if (c.__a11yNavChecked) return;
        if (!owlInstance(c)) { allResolved = false; return; }
        injectCarouselNav(c);
        c.__a11yNavChecked = true;
      });

      if (allResolved || tries >= 30) window.clearInterval(timer); // ~15s ceiling
    }, 500);
  }
})();
